import axios from 'axios';
import isObject from 'lodash/isPlainObject';
import { getCustomerType, getFuelType } from './api-utils';

// Create Cancel method for use in the axios xhr
let cancel = () => {}; // noop by default

/**
 * Mutates valid params for the HTTP call
 *
 * @param {Object} params
 * @return {Object}
 */
function createApiParams(params) {
  const {
    isResidential,
    searchByPostcode,
    postcode,
    servicesRequired,
    hasSolarPanels,
    connectionScenario,
    addressId,
    useCache,
  } = params;

  const address = searchByPostcode === false ? { addressId } : { postcode };

  return {
    ...address,
    customerType: getCustomerType(isResidential),
    searchByPostcode,
    fuelType: getFuelType(servicesRequired),
    hasSolarPanels,
    connectionScenario,
    useCache,
  };
}
//getbpidproducts?isResidential=true&addressId=NGW0007145754&campaignId=C-00041655&divisionId=01
function createBPIDApiParams(params) {
  const { isResidential, userAddressId, campaignId, divisionId } = params;

  return {
    customerType: isResidential ? 'residential' : 'business',
    addressId: userAddressId,
    campaignId,
    divisionId,
  };
}

/**
 * Ensure required params are present and mutates them for the HTTP call
 *
 * @param {Object} params
 * @return {Object}
 */
export function validateApiParams(params) {
  if (!isObject(params)) {
    return new TypeError('params is not of required type `Object`');
  }

  // validate required parameters
  if (typeof params.searchByPostcode !== 'boolean') {
    return new TypeError('params.searchByPostcode is not of required type `Boolean`');
  }

  if (params.searchByPostcode === false) {
    if (typeof params.addressId !== 'string') {
      return new TypeError('params.addressId is not of required type `String`');
    }
  } else {
    if (typeof params.postcode !== 'string') {
      return new TypeError('params.postcode is not of required type `String`');
    }
  }

  if (typeof params.isResidential !== 'boolean' || typeof params.isBusiness !== 'boolean') {
    return new RangeError(
      'Either params.isBusiness or params.isResidential is not of required type `Boolean`',
    );
  }

  if (!isObject(params.servicesRequired)) {
    return new TypeError('params.servicesRequired is not of required type `Object`');
  }

  if (typeof params.connectionScenario !== 'string') {
    return new TypeError('params.connectionScenario is not of required type `String`');
  }
}

export function validateBPIDApiParams(params) {
  if (!isObject(params)) {
    return new TypeError('params is not of required type `Object`');
  }

  if (typeof params.isResidential !== 'boolean' || typeof params.isBusiness !== 'boolean') {
    return new RangeError(
      'Either params.isBusiness or params.isResidential is not of required type `Boolean`',
    );
  }

  if (typeof params.campaignId !== 'string' && params.campaignId.string !== '') {
    return new TypeError('params.campaignId is not of required type `String`');
  }

  if (typeof params.userAddressId !== 'string' && params.userAddressId !== '') {
    return new TypeError('params.userAddressId is not of required type `String`');
  }
}

/**
 * Creates HTTP request.
 Wrapped in Promise to reject when param values not valid. Consumer can choose to fail silently.
 * @param {String} url (required)
 * @param {Object} params (required)
 * @typedef {Object} params
 * @property {Boolean} isResidential (required)
 * @property {Boolean} isBusiness (required)
 * @property {Boolean} searchByPostcode (required)
 * @property {String} postcode (required)
 * @property {Array.<string>} fuelType (required) - Accepted values: 'gas'|'electricity'|'dual'
 * @property {String} connectionScenario
 * @property {Boolean} hasSolarPanels
 * @param {Function} ajaxExecutor - default is locally imported lib
 * @return {Function} new Promise
 */
export function getPlans(
  url,
  params,
  ajaxExecutor = axios,
  trackError = trackError, // TODO: ensure this shouldn't be done at the app level, not in this AJAX service
) {
  if (!url || typeof url !== 'string') {
    throw new Error('url is required');
  }

  return new Promise((resolve, reject) => {
    const err = validateApiParams(params);
    if (err) {
      reject(err);
    } else {
      const CancelToken = axios.CancelToken;
      return ajaxExecutor({
        method: 'GET',
        url: url,
        params: createApiParams(params),
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
        .then((response) => {
          // The response object has changd a bit over time, but in some cases the codebase is
          // strongly in favour of a certain shape. We can easily and quickly reformat the response
          // object here, to save logic refactoring
          const data = {
            fuelEligibilityStatus: response.data.data.fuelEligibilityStatus || {},
            plans: Array.isArray(response.data.data.plans)
              ? response.data.data.plans.map((plan) => {
                  return {
                    ...plan,
                    identifier: plan.description,
                    displayInfo: {
                      main: plan.primaryDisplayFeatures || [],
                      supplementary: plan.secondaryDisplayFeatures || [],
                      charges: [],
                    },
                  };
                })
              : [], // fallback to an empty array
          };
          resetCancelToken();
          resolve(data);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            reject({
              message: 'cancelled',
            });
          }
          resetCancelToken();
          if (error.response && error.response.data && error.response.data.error) {
            // The request was made, but the server responded with a status code
            // that falls out of the range of 2xx
            reject({
              code: `${error.response.data.error.code}`,
              message: `${error.response.data.error.message}`,
            });
          } else {
            // Something happened in setting up the request that triggered an Error
            reject(error);
          }
        });
    }
  });
}

export function getBPIDInformation(url, apiKey, params, ajaxExecutor = axios) {
  return new Promise((resolve, reject) => {
    const err = validateBPIDApiParams(params);
    const requestParams = createBPIDApiParams(params);
    if (!url || typeof url !== 'string') {
      reject('url is required');
    }

    if (err) {
      reject(err);
    } else {
      const CancelToken = axios.CancelToken;
      return ajaxExecutor({
        method: 'GET',
        headers: { 'x-api-key': apiKey },
        url: url,
        params: requestParams,
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
        .then((response) => {
          response.data = response.data || {};
          const links = [];
          if (params.divisionId === '01') {
            links.push({
              url: response.data.elecUrl,
            });
          } else if (params.divisionId === '02') {
            links.push({
              url: response.data.gasUrl,
            });
          } else {
            links.push({
              suffix: '(Electricity)',
              url: response.data.elecUrl,
            });
            links.push({
              suffix: '(Gas)',
              url: response.data.gasUrl,
            });
          }

          const data = {
            links: links,
            campaignID: requestParams.campaignId,
            requestMapping: requestParams,
          };
          resetCancelToken();
          resolve(data);
        })
        .catch(() => {
          resolve(false);
        });
    }
  });
}

function resetCancelToken() {
  cancel = () => {};
}

export function getCancelToken() {
  return cancel;
}
